import React, { useContext, useRef, useEffect, useState } from "react"
import BlockContent from "@sanity/block-content-to-react"
import { VeraImage, VeraImageWide } from "../elements/vera-image"
import BreadCrumb from "../elements/breadcrumb"
import { Link } from "gatsby"
import { PreviewContext } from "../../context/preview-context"
import SocialSharing from "../elements/social-sharing"
import { StickyContainer, Sticky } from "react-sticky"
import AsideTeaser from "./aside-teaser"
import useElementDimensions from "../../hooks/use-element-dimensions"
import { linkResolver } from "../../util"

const serializers = {
  marks: {
    internalLink: ({ mark, children }) => {
      if (!mark?.reference) {
        return <>{children}</>
      }
      const { slug, _type } = mark.reference
      const href = linkResolver({
        slug: slug.current,
        _type,
        productSlug: mark.reference.product_collection
          ? mark.reference.product_collection.slug.current
          : "",
      })

      return <a href={href}>{children}</a>
    },
  },
}

const ContentLoop = ({
  content,
  breadCrumbSection,
  breadCrumbTitle,
  ignoreBreadcrumb,
  recipeImage,
  isRecipe,
}) => {
  const { isPreview } = useContext(PreviewContext)

  const ContentWrapper = ({ aside, content, isImage }) =>
    isImage ? (
      <div className="column is-12">{content()}</div>
    ) : (
      <>
        <div className="column is-4-tablet is-3-widescreen">
          {aside && <>{aside()}</>}
        </div>
        <div className="column">{content()}</div>
      </>
    )

  const contentMap = content => {
    const withoutIngredients = isRecipe ? content.slice(2) : content
    if (isRecipe) {
      if (recipeImage && recipeImage.video) {
        withoutIngredients.unshift({
          _type: "video",
          _key: "recipeInjectVideo",
          url: recipeImage.video,
        })
      } else {
        if (recipeImage) {
          withoutIngredients.unshift({
            ...recipeImage.image,
            _key: "recipeInjectImage",
            _type: "image",
          })
        }
      }
    }
    return withoutIngredients.map((current, i) => {
      if (current._type === "article_section") {
        const aside =
          i === 0 && !ignoreBreadcrumb ? (
            <div className="is-hidden-mobile">
              <SocialSharing />
              <BreadCrumb
                space
                section={breadCrumbSection}
                title={breadCrumbTitle}
              />
            </div>
          ) : null

        return (
          <div key={current._key} className="columns content-section rich-text">
            <ContentWrapper
              aside={() => {
                if (aside) {
                  return aside
                } else {
                  if (current.section_aside) {
                    return (
                      <div
                        style={{
                          maxWidth: "95%",
                          display: "flex",
                          justifyContent: "start",
                          fontStyle: "italic",
                          alignItems: "flex-end",
                          height: "100%",
                          paddingBottom: "1.2rem",
                          fontSize: "1.125rem",
                        }}
                      >
                        {current.section_aside}
                      </div>
                    )
                  }
                }
              }}
              content={() => {
                return (
                  <article
                    className="article-page__content"
                    style={isRecipe ? { marginRight: 0 } : {}}
                  >
                    {current.section_title && (
                      <h2
                        id={current.section_title.replace(/\s/g, "")}
                        className="h3"
                      >
                        {current.section_title}
                      </h2>
                    )}
                    {isPreview ? (
                      <BlockContent
                        blocks={current.section_contentRaw}
                        serializers={serializers}
                      />
                    ) : (
                      <BlockContent
                        blocks={current._rawSectionContent}
                        serializers={serializers}
                      />
                    )}
                  </article>
                )
              }}
            />
          </div>
        )
      } else if (current._type === "image") {
        return (
          <div key={current._key} className="columns">
            <ContentWrapper
              content={() => (
                <VeraImageWide
                  className={`content__image-wrapper${
                    isRecipe ? "--recipe" : ""
                  }`}
                  asset={current.asset}
                />
              )}
              isImage
            />
          </div>
        )
      } else if (current._type === "video" || current._type === "video_url") {
        const id = current.url.split("=")[1]
        return (
          <div key={current._key} className="columns">
            <ContentWrapper
              isImage
              content={() => (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    paddingBottom: "56.25%",
                  }}
                >
                  <iframe
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                    }}
                    className="embed-youtube"
                    src={`https://www.youtube-nocookie.com/embed/${id}`}
                    frameBorder="0"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            />
          </div>
        )
      }
    })
  }
  return <>{contentMap(content)}</>
}

export default ContentLoop
